import {
  projectTreeRuntimeMissingParentLifecycleAttributionPublication,
  sanitizeTreeRuntimeMissingParentLifecycleAttributionForPublication,
  TREE_RUNTIME_MISSING_PARENT_LIFECYCLE_ATTRIBUTION_EVIDENCE_SOURCE,
  type TreeRuntimeLifecyclePublicationSharedSeverity,
} from './TreeRuntimeMissingParentLifecycleAttributionPublicationContract';

import type {
  TreeRuntimeLifecycleAttributionStatus,
  TreeRuntimeLifecycleBrowserOwnerMode,
} from './TreeRuntimeMissingParentLifecycleAttributionParity';

export const TREE_RUNTIME_MISSING_PARENT_LIFECYCLE_OPERATIONAL_OBSERVABILITY_MILESTONE =
  'CC-TREE-LIVE-003G2O' as const;

export type TreeRuntimeMissingParentLifecycleOperationalAvailability =
  | 'NOT_OBSERVED'
  | 'OBSERVED';

export interface TreeRuntimeMissingParentLifecycleOperationalCounts {
  readonly browserMissingReferenceParentIdsCount:
    number;

  readonly serverMissingReferenceParentIdsCount:
    number;

  readonly softDeletedSameGraphCount:
    number;

  readonly indeterminateCount:
    number;

  readonly activeSameGraphCount: 0;
  readonly crossGraphCount: 0;
  readonly physicallyAbsentCount: 0;
}

export interface TreeRuntimeMissingParentLifecycleOperationalSnapshot {
  readonly milestone:
    typeof TREE_RUNTIME_MISSING_PARENT_LIFECYCLE_OPERATIONAL_OBSERVABILITY_MILESTONE;

  readonly source:
    typeof TREE_RUNTIME_MISSING_PARENT_LIFECYCLE_ATTRIBUTION_EVIDENCE_SOURCE;

  readonly availability:
    TreeRuntimeMissingParentLifecycleOperationalAvailability;

  readonly revision:
    number;

  readonly observedAt:
    string | null;

  readonly attributionStatus:
    TreeRuntimeLifecycleAttributionStatus | null;

  readonly browserOwnerResolutionMode:
    TreeRuntimeLifecycleBrowserOwnerMode | null;

  readonly severity:
    TreeRuntimeLifecyclePublicationSharedSeverity | null;

  readonly summary:
    string;

  readonly projectionFingerprint:
    string | null;

  readonly lifecycleCountsPublished:
    boolean;

  readonly lifecycleCounts:
    TreeRuntimeMissingParentLifecycleOperationalCounts | null;

  readonly identityParityProven: false;
  readonly readOnly: true;
  readonly repairExecuted: false;
  readonly repairReady: false;
}

const listeners =
  new Set<() => void>();

let revision =
  0;

let snapshot:
  TreeRuntimeMissingParentLifecycleOperationalSnapshot =
  Object.freeze({
    milestone:
      TREE_RUNTIME_MISSING_PARENT_LIFECYCLE_OPERATIONAL_OBSERVABILITY_MILESTONE,

    source:
      TREE_RUNTIME_MISSING_PARENT_LIFECYCLE_ATTRIBUTION_EVIDENCE_SOURCE,

    availability:
      'NOT_OBSERVED',

    revision: 0,
    observedAt: null,
    attributionStatus: null,
    browserOwnerResolutionMode: null,
    severity: null,

    summary:
      'No lifecycle attribution runtime publication has been observed in this runtime.',

    projectionFingerprint: null,
    lifecycleCountsPublished: false,
    lifecycleCounts: null,

    identityParityProven: false,
    readOnly: true,
    repairExecuted: false,
    repairReady: false,
  });

const notify =
  (): void => {
    for (const listener of listeners) {
      try {
        listener();
      } catch {
        // Presentation subscriber failure is non-fatal.
      }
    }
  };

export function getTreeRuntimeMissingParentLifecycleOperationalRevision():
  number {
  return revision;
}

export function subscribeTreeRuntimeMissingParentLifecycleOperationalObservation(
  listener: () => void,
): () => void {
  listeners.add(listener);

  return () => {
    listeners.delete(listener);
  };
}

export function readTreeRuntimeMissingParentLifecycleOperationalObservation():
  TreeRuntimeMissingParentLifecycleOperationalSnapshot {
  return snapshot;
}

const validObservedAt =
  (
    value: unknown,
  ): value is string => {
    if (
      typeof value !== 'string' ||
      value.length === 0
    ) {
      return false;
    }

    try {
      return (
        new Date(value).toISOString() ===
        value
      );
    } catch {
      return false;
    }
  };

export interface TreeRuntimeMissingParentLifecycleOperationalObservationInput {
  readonly decision:
    unknown;

  readonly observedAt:
    string;
}

export function publishTreeRuntimeMissingParentLifecycleOperationalObservation(
  input:
    TreeRuntimeMissingParentLifecycleOperationalObservationInput,
): boolean {
  if (!validObservedAt(input.observedAt)) {
    return false;
  }

  const decision =
    sanitizeTreeRuntimeMissingParentLifecycleAttributionForPublication(
      input.decision,
    );

  if (!decision) {
    return false;
  }

  const projection =
    projectTreeRuntimeMissingParentLifecycleAttributionPublication(
      decision,
    );

  if (!projection) {
    return false;
  }

  const lifecycle =
    decision.missingParentReferenceLifecycle;

  const browserCount =
    decision.browserMissingReferenceParentIdsCount;

  const serverCount =
    decision.serverMissingReferenceParentIdsCount;

  let lifecycleCounts:
    TreeRuntimeMissingParentLifecycleOperationalCounts | null =
    null;

  if (projection.lifecycleCountsPublished) {
    if (
      lifecycle === null ||
      typeof browserCount !== 'number' ||
      typeof serverCount !== 'number'
    ) {
      return false;
    }

    lifecycleCounts =
      Object.freeze({
        browserMissingReferenceParentIdsCount:
          browserCount,

        serverMissingReferenceParentIdsCount:
          serverCount,

        softDeletedSameGraphCount:
          lifecycle
            .missingParentReferenceSoftDeletedSameGraphCount,

        indeterminateCount:
          lifecycle
            .missingParentReferenceIndeterminateCount,

        activeSameGraphCount: 0,
        crossGraphCount: 0,
        physicallyAbsentCount: 0,
      });
  }

  revision += 1;

  snapshot =
    Object.freeze({
      milestone:
        TREE_RUNTIME_MISSING_PARENT_LIFECYCLE_OPERATIONAL_OBSERVABILITY_MILESTONE,

      source:
        TREE_RUNTIME_MISSING_PARENT_LIFECYCLE_ATTRIBUTION_EVIDENCE_SOURCE,

      availability:
        'OBSERVED',

      revision,
      observedAt:
        input.observedAt,

      attributionStatus:
        projection.attributionStatus,

      browserOwnerResolutionMode:
        projection.browserOwnerResolutionMode,

      severity:
        projection.sharedSeverity,

      summary:
        projection.summary,

      projectionFingerprint:
        projection.fingerprint,

      lifecycleCountsPublished:
        projection.lifecycleCountsPublished,

      lifecycleCounts,

      identityParityProven: false,
      readOnly: true,
      repairExecuted: false,
      repairReady: false,
    });

  notify();

  return true;
}
